// Pure decision helpers for the Libby insights strip and modal.
//
// This module is deliberately free of DOM/global access so the logic can be
// exercised by the node test runner; the DOM wiring lives in ai_explain.js.
// `explain_issues` is imported with its .js extension so node resolves it
// directly in tests (bundlers accept either form).
import { isGoodStatusIssue } from './explain_issues.js';

const SEVERITY_RANK = { good: 0, info: 1, warn: 2, bad: 3 };
const MAX_STRIP_LABELS = 3;

export function normalizeSeverity(raw) {
    const value = String(raw || "").toLowerCase();
    if (["bad", "red", "critical", "error", "danger"].includes(value)) return "bad";
    if (["warn", "warning", "orange", "amber"].includes(value)) return "warn";
    if (["good", "ok", "success", "green"].includes(value)) return "good";
    return "info";
}

export function worseSeverity(a, b) {
    return (SEVERITY_RANK[b] || 0) > (SEVERITY_RANK[a] || 0) ? b : a;
}

export function isGoodStatusNote(entry) {
    return entry.severity === "info" && isGoodStatusIssue(entry.issue);
}

// Turn a raw region/issue pair into a short label, e.g.
// "Site Heatmap — TCP Retransmits: TCP retransmits high" -> "High Retransmits".
export function shortIssueLabel(entry) {
    const region = String(entry.region || "");
    const issue = String(entry.issue || "");
    if (/no upgrade recommended/i.test(issue)) return null;
    let metric = region.includes("—") ? region.split("—").pop().trim() : region.trim();
    metric = metric
        .replace(/\bheatmap\b/gi, "")
        .replace(/^TCP\s+/i, "")
        .replace(/\s+/g, " ")
        .trim();
    // The "Capacity" heatmap shows utilization, so "High Capacity" would read
    // backwards ("lots of spare capacity"). Call it utilization instead.
    if (metric.toLowerCase() === "capacity") metric = "Utilization";
    const lower = issue.toLowerCase();
    // Throughput capacity summaries describe saturation rather than a "high"
    // metric, e.g. "Download approached capacity … (over limit …)".
    if (/approached capacity|near capacity|over limit/.test(lower)) return "Near capacity";
    if (/\bhigh\b/.test(lower)) return `High ${metric || "usage"}`;
    if (/\blow\b/.test(lower)) return `Low ${metric || "usage"}`;
    if (/upgrade/i.test(region) && /recommend/i.test(issue)) return "Upgrade candidate";
    return metric || issue || null;
}

export function summarizeEntries(entries) {
    const real = entries.filter((entry) => !isGoodStatusNote(entry));
    if (real.length === 0) {
        return { severity: "good", status: "Everything looks good.", labels: [] };
    }
    let severity = "info";
    real.forEach((entry) => { severity = worseSeverity(severity, entry.severity); });

    // Most severe first, de-duplicated short labels (instead of repeating the
    // full "region: issue" text for every bucket).
    const labels = [];
    real.slice()
        .sort((a, b) => (SEVERITY_RANK[b.severity] || 0) - (SEVERITY_RANK[a.severity] || 0))
        .forEach((entry) => {
            const label = shortIssueLabel(entry);
            if (label && !labels.includes(label)) labels.push(label);
        });
    return { severity, status: "", labels: capLabels(labels) };
}

// Flatten the structured groups into display rows.
export function groupEntries(groups) {
    const entries = [];
    (groups || []).forEach((group) => {
        (group.issues || []).forEach((entry) => {
            entries.push({
                region: group.region ? String(group.region) : "",
                issue: entry.issue ? String(entry.issue) : "",
                time_range: entry.time_range ? String(entry.time_range) : null,
                severity: normalizeSeverity(entry.severity),
            });
        });
    });
    return entries;
}

export function legacyEntries(issues) {
    return (issues || []).map((entry) => ({
        region: entry.dashlet_name ? String(entry.dashlet_name) : "",
        issue: entry.description ? String(entry.description) : "",
        time_range: null,
        // Legacy prose carries no structured severity, so treat it as a neutral
        // note rather than assuming it is a problem (some, e.g. "No upgrade
        // recommended", describe good outcomes).
        severity: "info",
    }));
}

// A legacy row is a mirror when `pushGroup` generated it from a structured
// group issue: same region and the structured issue text appears in the prose.
// This mirrors the backend's `non_mirrored_legacy_issues` rule so genuine
// prose-only issues (Shaper CPU/Memory, upgrade analysis) survive the
// de-duplication.
function isMirroredLegacyEntry(entry, groups) {
    return (groups || []).some((group) => {
        if (!group || String(group.region || "") !== entry.region) return false;
        return (group.issues || []).some((detail) => {
            const issue = detail && detail.issue ? String(detail.issue) : "";
            // An empty structured issue would be "contained" in every
            // description and hide a real prose-only issue.
            if (!issue.trim()) return false;
            return entry.issue.includes(issue);
        });
    });
}

// Display rows: the structured groups first, then the legacy prose rows that
// are not mirrors of a group. The legacy list is always kept as a fallback for
// older senders, so a prose-only issue is never dropped just because some
// dashlet produced a group.
export function flattenInsights(groups, issues) {
    const grouped = groupEntries(groups);
    const legacy = legacyEntries(issues);
    if (grouped.length === 0) return legacy;
    return grouped.concat(legacy.filter((entry) => !isMirroredLegacyEntry(entry, groups)));
}

export function insightsSeverity(insights) {
    let severity = "good";
    (insights || []).forEach((insight) => {
        severity = worseSeverity(severity, normalizeSeverity(insight && insight.severity));
    });
    return severity;
}

export function insightLabels(insights) {
    const labels = [];
    (insights || []).forEach((insight) => {
        const label = insight && insight.label ? String(insight.label).trim() : "";
        if (label && !labels.includes(label)) labels.push(label);
    });
    return labels;
}

export function hasInsights(value) {
    return Array.isArray(value) && value.length > 0;
}

// Decide what the at-a-glance strip shows. A non-empty backend insight payload
// takes precedence; anything else (null, or a legacy empty array) falls back to
// the local structured entries. The empty-array case must NOT be treated as
// "backend says all good": an older/other backend may omit insights even while
// the local dashlets report warnings.
export function stripViewFor(entries, insights, headline = "") {
    if (hasInsights(insights)) {
        const count = insights.length;
        return {
            source: "insights",
            severity: insightsSeverity(insights),
            labels: capLabels(insightLabels(insights)),
            status: headline || (count === 1
                ? "1 thing to review."
                : `${count} things to review.`),
        };
    }
    const summary = summarizeEntries(Array.isArray(entries) ? entries : []);
    return {
        source: "local",
        severity: summary.severity,
        labels: summary.labels,
        status: summary.status,
    };
}

function capLabels(labels) {
    const visible = labels.slice(0, MAX_STRIP_LABELS);
    if (labels.length > MAX_STRIP_LABELS) visible.push(`+${labels.length - MAX_STRIP_LABELS} more`);
    return visible;
}

// Screen-reader-distinguishable heading text for an insight: the label alone
// repeats across directions (e.g. "High Retransmits" for upload and download),
// so append the direction when the backend provides one.
export function insightHeading(insight) {
    const label = insight && insight.label ? String(insight.label).trim() : "";
    const base = label || "Insight";
    const direction = insight && insight.direction ? String(insight.direction).toLowerCase() : "";
    if (direction === "download") return `${base}, download`;
    if (direction === "upload") return `${base}, upload`;
    if (direction === "both") return `${base}, upload and download`;
    return base;
}

// Libby's narrative markdown may contain h1-h5 headings. Inside the modal body
// those compete with the page's own heading hierarchy, so demote them to h6.
// Case-insensitive, attributes preserved, other content untouched.
export function demoteHeadings(html) {
    return String(html == null ? "" : html)
        .replace(/<(\/?)h([1-5])(\s[^>]*)?>/gi, "<$1h6$3>");
}
